import { CostBasisPosition } from '@interfaces/cost-basis/cost-basis.service.interface';
import { FraudOutcome } from '@interfaces/fraud-detector/fraud-detector.interface';
import {
  CO6ReceiptDTO,
  InternalTransferReceiptDTO,
  IReceiptDTO,
  ISelectedClientAccountsState,
} from '@interfaces/models';
import { OneTimePasscodeStatus } from '@interfaces/one-time-passcode/otp.interface';
import {
  DecedentInfoState,
  FeatureToggleState,
  GiftingState,
  InternalTransferState,
  IOpenAccountState,
  TriageState,
} from '@interfaces/state';
import { BeneficiariesState } from '@store/reducers/beneficiaries/beneficiaries.reducer';
import { EstateState } from '@store/reducers/estate/estate.reducer';
import { TCFTrustState } from '@store/reducers/tcf-trust/tcf-trust.reducer';
import { TrustState } from '@store/reducers/trust/trust.reducer';
import { TrustTaxInfoState } from '@store/reducers/trust-tax-info/trust-tax-info.reducer';
import { ConsumerPayloadsType } from '@vanguard/fmf-common/dist/models/types/consumer-payloads';
import { VgEmploymentState, VgimAddress } from 'vg-interaction-model';
import { IPersonalPhoneState } from 'vg-interaction-model/dist/lib/features/personal-phone/store/reducers/personal-phone.reducer';

export interface S3FileInformation {
  filename: string;
  fileExtension: string;
}

export declare enum RecaptchaSimulatorOptions {
  DEFAULT = 'DEFAULT',
  PASS = 'PASS',
  ERROR = 'ERROR',
  FAIL = 'FAIL',
}
export interface RecaptchaMockResult {
  result: RecaptchaSimulatorOptions;
  errorMessage?: string[];
}
export interface FMFResponse {
  action: string;
  transactionId: string;
  tokens: ConsumerPayloadsType;
}
export interface RegularISubmit {
  transactionType:
    | Co6TransactionTypes.gifting
    | Co6TransactionTypes.inheritance
    | Co6TransactionTypes.minor;
  config: ISubmitConfig;
  receiptDTO: IReceiptDTO | CO6ReceiptDTO;
  fmfResponse?: FMFResponse;
  oaFMFResponse?: FMFResponse;
  mockRecaptchaLevel?: RecaptchaMockResult;
  mockFraudDetectorLevel?: FraudOutcome;
  otpVerificationStatus: OneTimePasscodeStatus;
  daEnrollPageHtml?: string;
}

export interface InternalTransferSubmit {
  transactionType: Co6TransactionTypes.internalTransfer;
  itConfig: InternalTransferSubmitConfig;
  receiptDTO: InternalTransferReceiptDTO;
  fmfResponse?: FMFResponse;
  oaFMFResponse?: FMFResponse;
  mockRecaptchaLevel?: RecaptchaMockResult;
  mockFraudDetectorLevel?: FraudOutcome;
  otpVerificationStatus: OneTimePasscodeStatus;
}

export enum Co6TransactionTypes {
  minor = 'MINOR',
  gifting = 'GIFTING',
  inheritance = 'INHERITANCE',
  internalTransfer = 'INTERNAL_TRANSFER',
}

export type ISubmit = RegularISubmit | InternalTransferSubmit;

export interface GiftingSubmitConfigPayload extends GiftingState {
  selectedPositions: CostBasisPosition[];
}

export interface ISubmitConfig {
  base64Pdf?: string;
  personalData: {
    ownerName: string;
    segment: string;
    dateOfBirth?: string;
    isSpouse?: string;
    accountNumber?: string;
    accountId?: string;
    citizenshipStatus?: string;
  };
  decedentInfo: SubmitDecedentInfoState;
  featureToggles: FeatureToggleState;
  triage: TriageState;
  openAccountState: IOpenAccountState;
  selectedAccs: ISelectedClientAccountsState;
  gifting?: GiftingSubmitConfigPayload;
  userType?: string;
  estateInfo?: EstateState;
  trust?: TrustState;
  trustTaxInfo?: TrustTaxInfoState;
  tcfTrust?: TCFTrustState;
  employment?: {
    associations: VgEmploymentState['associations'];
    employmentStatus: VgEmploymentState['employmentStatus'];
  };
  mailingAddress?: VgimAddress;
  streetAddress?: VgimAddress;
  phone?: IPersonalPhoneState;
  beneficiaries?: BeneficiariesState;
  caseInformationFiles?: S3FileInformation[];
  filenetCaptureFiles?: S3FileInformation[];
}

export interface SubmitDecedentInfoState extends DecedentInfoState {
  accountType?: string;
}

export interface InternalTransferSubmitConfig {
  personalData: { ownerName: string; segment: string };
  featureToggles: FeatureToggleState;
  internalTransfer: InternalTransferState;
  openAccountState: IOpenAccountState;
  selectedPositions: CostBasisPosition[];
}

export interface SubmitLoggerConfig {
  userPoid: string;
  userType: string;
  transactionType: string;
  segment: string;
}

export interface SubmitLoggerConfig {
  userPoid: string;
  userType: string;
  transactionType: string;
  segment: string;
  transferFrom?: string;
  transferTo?: string;
}

export interface ITSubmitLoggerConfig {
  userPoid: string;
  userType: string;
  transactionType: string;
  segment: string;
  amountTransacted: string;
  transferFrom: string;
  transferTo: string;
  transferMethod: string;
  transferType: string;
}

export type SubmitLoggerInterface = SubmitLoggerConfig | ITSubmitLoggerConfig;

export interface CO6Response {
  status: number;
  data: STPResponse;
}

export interface AppianCWPSuccessResponse {
  success: boolean;
  appianSuccess: AppianSuccessResponse;
}

export interface AppianCWPErrorResponse {
  success: boolean;
  appianFailed: any;
}
export interface STPResponse {
  transactId: string;
}

export type SubmitResponse = AppianCWPSuccessResponse | STPResponse;

export interface SubmitErrorResponse {
  eventId: string;
}

export interface AppianSuccessResponse {
  statusCode: number;
  bpmCaseId?: string[];
  bpmEnvelopeId?: number;
  createDateTime?: string;
}
